import { Checkbox } from "@babackapp/customer-ui/components/checkbox";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnFinancials } from "@babackapp/return-widget-components/hooks/use-return-financials";
import { useSetNotifyCustomer } from "@babackapp/return-widget-components/hooks/use-set-notify-customer";
import { useSetReturnShippingPaid } from "@babackapp/return-widget-components/hooks/use-set-return-shipping-paid";
import { useSetWithShippingLabel } from "@babackapp/return-widget-components/hooks/use-set-with-shipping-label";
import { useTranslation } from "react-i18next";
import { useIsMerchant } from "../hooks/use-is-merchant";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useWidgetParams } from "../hooks/use-widget-params";

/**
 * Merchant-only overrides shown on the confirmation screen: skip the customer
 * notification, skip the shipping-label generation, and/or waive the return fee.
 * Mirrors the operator checkboxes from the legacy widget (ConfirmReturn.tsx,
 * refund-details-section.tsx). Hidden for customers.
 */
export function MerchantReturnOptions({ orderId }: { orderId: string }) {
  const isMerchant = useIsMerchant();
  const { t } = useTranslation("return-widget-v3");
  const { shopId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const draft = useDraftReturn(orderId);
  const financials = useReturnFinancials(orderId, shopId, order ?? null);
  const setNotifyCustomer = useSetNotifyCustomer(orderId);
  const setWithShippingLabel = useSetWithShippingLabel(orderId);
  const setReturnShippingPaid = useSetReturnShippingPaid(orderId);

  if (!isMerchant || !draft) return null;

  const showLabelOption =
    draft.returnMode !== "store" && draft.returnMode !== "retractation";
  const showFeeOption = financials.isFeePending || financials.grossFee > 0;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-admin-border bg-admin/5 p-4">
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <Checkbox
          checked={draft.notifyCustomer ?? true}
          onCheckedChange={(checked) => setNotifyCustomer(checked === true)}
        />
        {t("merchantOptions.notifyCustomer")}
      </label>
      {showLabelOption && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={draft.withShippingLabel ?? true}
            onCheckedChange={(checked) => setWithShippingLabel(checked === true)}
          />
          {t("merchantOptions.generateLabel")}
        </label>
      )}
      {showFeeOption && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={draft.returnShippingPaid ?? true}
            onCheckedChange={(checked) => setReturnShippingPaid(checked === true)}
          />
          {t("merchantOptions.chargeReturnFee")}
        </label>
      )}
    </div>
  );
}
